import { Injectable } from '@angular/core';
import { DownloadOption, DownloadTarget } from '../domain/blockly/blockly.types';
import { DEFAULT_CODE_LANG_TARGETS, MODULE_TARGET_OVERRIDES, TARGET_LABELS, TARGET_LANG_CONSTRAINTS } from '../domain/blockly/download.config';
import { getCodeLangConfig } from '../domain/code-gen/code-lang.utils';
import { SupportedCodeLang } from '../domain/code-gen/code-lang.types';

@Injectable({ providedIn: 'root' })
export class DownloadOptionsService {

  computeOptions(
    codeLang: SupportedCodeLang,
    moduleKey: string,
    hideGatewayOption: boolean
  ): DownloadOption[] {
    const targets = this.resolveTargets(moduleKey, codeLang);

    const availableTargets = targets.filter((target) =>
      this.isTargetAvailable(target, codeLang, hideGatewayOption)
    );

    return availableTargets.map((target) => ({
      key: target,
      display: this.getDisplayLabel(target, codeLang),
    }));
  }

  private resolveTargets(
    moduleKey: string,
    codeLang: SupportedCodeLang
  ): readonly DownloadTarget[] {
    const moduleOverride = MODULE_TARGET_OVERRIDES[moduleKey]?.[codeLang];

    if (moduleOverride) {
      return moduleOverride;
    }

    return DEFAULT_CODE_LANG_TARGETS[codeLang];
  }

  private isTargetAvailable(
    target: DownloadTarget,
    codeLang: SupportedCodeLang,
    hideGatewayOption: boolean
  ): boolean {
    if (hideGatewayOption && target === DownloadTarget.RaspberryPi) {
      return false;
    }

    const allowedCodeLangs = TARGET_LANG_CONSTRAINTS[target];
    if (allowedCodeLangs && !allowedCodeLangs.includes(codeLang)) {
      return false;
    }

    return true;
  }

  private getDisplayLabel(
    target: DownloadTarget,
    codeLang: SupportedCodeLang
  ): string {
    return TARGET_LABELS[target] ?? getCodeLangConfig(codeLang)?.displayLabel ?? target;
  }

  compareOptions(a: DownloadOption[], b: DownloadOption[]): boolean {
    if (a.length !== b.length) return false;
    return a.every((opt, i) => opt.key === b[i].key && opt.display === b[i].display);
  }

  hasRpiSupport(codeLang: SupportedCodeLang, moduleKey: string): boolean {
    const targets = this.resolveTargets(moduleKey, codeLang);
    return targets.includes(DownloadTarget.RaspberryPi);
  }

  isGatewayTarget(target: DownloadTarget | string): boolean {
    return target === DownloadTarget.RaspberryPi;
  }
}